/* Shared readiness + value-presence rules for the four summary cards.
 *
 * Both card surfaces -- the Power Flow grid (CardsPage) and the flat header
 * row on the Charts tab (CardsRow) -- render the same four cards off the same
 * context, so they share one gate rather than each inventing their own.
 *
 * The problem this solves: ExportedDataContext seeds the summary objects with
 * literal "0.00" strings, so before a worker reports, a card's value is
 * indistinguishable from a value the engine genuinely computed as zero. A user
 * has already reported a real accounting bug that surfaced as $0.00, which
 * means a placeholder zero reads to them exactly like a defect. Readiness
 * therefore has to come from an arrival signal, never from the values.
 */

import {
  formatWithCommas,
  cardDigitsFor,
  isBelowCardPrecision,
} from "../../utils/formatNumber";

/* The em-dash already used for demand-charge attribution that a run did not
 * record. Same reasoning, same glyph: absent is not zero. */
export const ABSENT_VALUE = "\u2014";

/* A value the engine measured but which is too small to show even at the
 * card's two-decimal precision. Distinct from the em-dash (nothing was
 * recorded) and from 0 (recorded as exactly zero). The unit or currency comes
 * from the column heading, so this carries neither. */
export const BELOW_PRECISION = "0.01";

/* The chart sources that actually feed the four cards.
 *
 * `pricing` is deliberately NOT here. chartDataReadyStates carries a `pricing`
 * key, but setExportedPricingData has no production caller at all (only a test
 * mock), so it is false forever; any gate that required it could never be
 * satisfied and the cards would stay skeletal for the life of the page. This
 * mirrors the `required` list in VerificationPage.jsx. */
export const SUMMARY_CARD_SOURCES = [
  "transfer",
  "production",
  "consumption",
  "storage",
];

/* True once the simulation has reported anything at all.
 *
 * ANY source, not ALL: the four sources land in separate effects (and, for
 * production and storage, behind a worker round-trip), and a customer with no
 * devices of a given kind can legitimately leave one quiet. Requiring all four
 * would be a second way to get stuck skeletal forever.
 *
 * shouldShowCards and hasMeaningfulData only ever *widen* readiness here, they
 * are never required. hasMeaningfulData is a non-zero test over the values, so
 * requiring it would leave a run the engine legitimately computed as all zeros
 * skeletal forever -- the exact case this gate has to render. It is accepted as
 * an alternative arrival signal for callers that forward only the derived
 * context flags.
 *
 * The flags are monotonic (they only ever go false -> true), so a card that has
 * rendered never falls back to a skeleton. */
export function isSummaryDataReady({
  chartDataReadyStates,
  shouldShowCards,
  hasMeaningfulData,
} = {}) {
  const ready = chartDataReadyStates || {};

  if (SUMMARY_CARD_SOURCES.some((source) => ready[source] === true)) {
    return true;
  }

  return Boolean(shouldShowCards) || Boolean(hasMeaningfulData);
}

/* Present means "the engine told us this number", which includes 0.
 *
 * Only null/undefined/"" and values that are not finite numbers count as
 * absent. A real 0 or "0.00" is a known value and must render as $0.00, never
 * as a skeleton and never as an em-dash. */
export function hasCardValue(value) {
  if (value === null || value === undefined || value === "") return false;
  return Number.isFinite(Number(value));
}

/* Rounding is the shared card policy in formatNumber.js -- whole numbers at 10
 * and above, two decimals below it -- so a real value can never round away to a
 * zero that would be indistinguishable from a measured one.
 *
 * One formatter, not one per unit. The unit now lives in the column heading
 * ("MAX (kW)", "TOTAL (kWh)", "VALUE ($)") rather than being repeated on every
 * figure, so every value cell on every card renders the same way and there is
 * nothing left for a per-unit variant to disagree about. */
export function formatCardNumber(value) {
  if (!hasCardValue(value)) return ABSENT_VALUE;
  if (isBelowCardPrecision(value)) {
    /* A magnitude statement, so the "<" stays on the negative form too:
     * "-0.01" would claim a measured one-cent cost. */
    return Number(value) < 0 ? `-<${BELOW_PRECISION}` : `<${BELOW_PRECISION}`;
  }
  return formatWithCommas(value, cardDigitsFor(value));
}
